import {
  createGrid,
  GridApi,
  GridOptions,
  ColDef,
  IRowNode,
  SelectionChangedEvent,
  ProcessCellForExportParams,
  ProcessRowGroupForExportParams,
  CsvCustomContent,
  ExcelRow,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../ag_grid_base_component/ag_grid_base_controller";
import { StatusCellRenderer } from "../ag_grid_shared/cell_renderers/status_cell_renderer";
import { ActionsCellRenderer } from "../ag_grid_shared/cell_renderers/actions_cell_renderer";
import { LinkCellRenderer } from "../ag_grid_shared/cell_renderers/link_cell_renderer";
import { MultilineTextCellRenderer } from "../ag_grid_shared/cell_renderers/multiline_text_cell_renderer";
import { ebTheme } from "../ag_grid_shared/eb_theme";
import { ebFilterParams, ebLocaleText } from "../ag_grid_shared/filter_defaults";
import { withDateFilterComparator } from "../ag_grid_shared/filters/date_string_comparator";
import { withNumberComparator } from "../ag_grid_shared/sorters/number_comparator";
import { formatCellValue, formatGroupCellValue } from "../ag_grid_shared/export_utils";
import { showConfirmModal, ConfirmModalOptions } from "../ag_grid_shared/confirm_modal";

export default class extends AgGridBaseController {
  static targets = [
    ...AgGridBaseController.targets,
    "quickFilterInput",
  ];

  static values = {
    ...AgGridBaseController.values,
    rowDataUrl:            String,
    rowData:               Array,
    advancedRowDataUrl:    String,
    basicModeOptions:      Object,
    advancedModeOptions:   Object,
    footerCounts:          Object,
    exportTitle:           String,
  };

  declare readonly quickFilterInputTarget: HTMLInputElement;
  declare readonly hasQuickFilterInputTarget: boolean;

  declare readonly rowDataUrlValue: string;
  declare readonly rowDataValue: Record<string, unknown>[];
  declare readonly advancedRowDataUrlValue: string;
  declare readonly basicModeOptionsValue: Partial<GridOptions>;
  declare readonly advancedModeOptionsValue: Partial<GridOptions>;
  declare readonly footerCountsValue: {
    labelField?: string;
    sumFields?: string[];
    countLabel?: string;
  };
  declare readonly exportTitleValue: string;

  private headerContextMenuBlocker: ((e: MouseEvent) => void) | null = null;
  private hasActiveRange = false;
  private saveGridStateTimer: ReturnType<typeof setTimeout> | null = null;
  private totalRowCount = 0;
  // Incremented each time a new data fetch starts so in-flight responses can
  // detect they've been superseded (e.g., mode switch during load).
  private dataFetchGeneration = 0;
  // Holds AG Grid state to apply after Advanced column defs + row data finish loading.
  private pendingRestoreState: ReturnType<GridApi["getState"]> | null = null;
  // Opt-in `grid:refresh` listener (spec 011 Decision C), scoped to this element so each
  // grid on the page refreshes only itself. Arrow property for add/removeEventListener symmetry.
  private handleGridRefresh = (): void => {
    this.refreshRowData();
  };

  connect() {
    super.connect(); // license key + outside click handler

    this.element.addEventListener("grid:refresh", this.handleGridRefresh);

    const hasAdvanced = this.advancedColumnDefsValue.length > 0;
    const sideBarConfig = this.buildSideBarConfig(hasAdvanced);
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};

    const options: GridOptions = {
      theme: ebTheme,
      localeText: ebLocaleText,
      columnDefs: withNumberComparator(
        withDateFilterComparator(
          this.withRankComparator(
            this.withActionsValueFormatter(
              hasAdvanced ? this.basicColDefs(this.columnDefsValue) : this.columnDefsValue
            )
          )
        )
      ),
      sideBar: sideBarConfig,
      enableFilterHandlers: true,
      autoGroupColumnDef: {
        comparator: (valueA: unknown, valueB: unknown, nodeA: IRowNode, nodeB: IRowNode) => {
          const field = nodeA.field ?? "";
          const colDef = field ? this.gridApi?.getColumn(field)?.getColDef() : null;
          if ((colDef?.context as Record<string, unknown>)?.["countSort"]) {
            return (nodeA.allLeafChildren?.length ?? 0) - (nodeB.allLeafChildren?.length ?? 0);
          }
          return String(valueA ?? "").localeCompare(String(valueB ?? ""));
        },
      },
      components: {
        statusCellRenderer: StatusCellRenderer,
        actionsCellRenderer: ActionsCellRenderer,
        linkCellRenderer: LinkCellRenderer,
        multilineTextCellRenderer: MultilineTextCellRenderer,
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        this.handleSelectionChanged(event);
      },
      onCellSelectionChanged: () => {
        this.handleRangeSelectionChanged();
      },
      onColumnRowGroupChanged: () => {
        this.autoSizeNonFlexColumns();
      },
      onRowGroupOpened: () => {
        this.autoSizeNonFlexColumns();
      },
      onModelUpdated: () => {
        if (this.pendingAutoSize) {
          this.pendingAutoSize = false;
          this.autoSizeNonFlexColumns();
        }
        this.refreshFooterRow();
      },
      onStateUpdated: () => {
        this.scheduleSaveGridState();
      },
      getMainMenuItems: (params) => this.withPivotAwareResetColumns(params),
      processCellForClipboard: (params: ProcessCellForExportParams) => formatCellValue(params),
      ...this.gridOptionsValue,
      // Placed after the gridOptionsValue spread so the shared filterParams default
      // survives even though each page supplies its own defaultColDef via Ruby.
      defaultColDef: { filterParams: ebFilterParams, ...baseColDef },
    };

    // getDataPath can't come through as JSON from Ruby, so default it here when opted in.
    if (this.gridOptionsValue?.treeData && !options.getDataPath) {
      options.getDataPath = (data: Record<string, unknown>) => data.group_path as string[];
    }

    const rowSel = options.rowSelection as Record<string, unknown> | undefined;
    if (rowSel && typeof rowSel === "object") {
      rowSel.isRowSelectable = this.rowSelectableCallback;
    }

    if (!this.hasGridTarget) return;

    this.gridApi = createGrid(this.gridTarget, options);
    this.syncDefaultExportParams();

    const isAutoHeight = this.gridOptionsValue?.domLayout === "autoHeight";
    if (!isAutoHeight) {
      this.initDynamicHeight();
    }

    this.loadGridState();

    this.setupFollowSelection();

    if (hasAdvanced) {
      this.isBasicMode = true;
      this.setBasicModeColumnOptions();
      this.headerContextMenuBlocker = (e: MouseEvent) => {
        if (this.isBasicMode && (e.target as HTMLElement).closest(".ag-header")) {
          e.stopImmediatePropagation();
          e.preventDefault();
        }
      };
      this.gridTarget.addEventListener("contextmenu", this.headerContextMenuBlocker, true);
    }

    if (this.rowDataUrlValue) {
      this.fetchRowData(this.rowDataUrlValue);
    } else if (this.rowDataValue.length > 0) {
      this.pendingAutoSize = true;
      this.gridApi.setGridOption("rowData", this.rowDataValue);
    }

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      const exportWrapper = this.hasExportToggleTarget
        ? this.exportToggleTarget.closest(".ag-grid-component-export-wrapper")
        : null;
      if (exportWrapper?.contains(target)) return;
      this.gridApi?.clearCellSelection();
      this.hasActiveRange = false;
      this.syncDefaultExportParams();
    };
    document.addEventListener("mousedown", this.gridBlurHandler);
  }

  disconnect() {
    // Clean up CSRM-only state before base destroys the grid
    if (this.saveGridStateTimer !== null) {
      clearTimeout(this.saveGridStateTimer);
      this.saveGridStateTimer = null;
    }
    this.pendingRestoreState = null;
    this.isRestoringState = false;
    if (this.headerContextMenuBlocker && this.hasGridTarget) {
      this.gridTarget.removeEventListener("contextmenu", this.headerContextMenuBlocker, true);
      this.headerContextMenuBlocker = null;
    }
    this.element.removeEventListener("grid:refresh", this.handleGridRefresh);
    super.disconnect(); // resize/click/mousedown listeners + gridApi.destroy()
  }

  // Opt-in, state-preserving refresh (spec 011 Decision C, FR-016): re-fetches row_data_url
  // and swaps only rowData (never columnDefs, unlike fetchQuestionData), so sort/filter/scroll
  // survive a poll-driven `grid:refresh`. No-op without row_data_url or a dispatched event.
  refreshRowData(): void {
    if (!this.gridApi || !this.rowDataUrlValue) return;
    const generation = ++this.dataFetchGeneration;
    fetch(this.rowDataUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to refresh row data: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (generation !== this.dataFetchGeneration || !this.gridApi) return;
        const rows = Array.isArray(json) ? json : (json.rows ?? []);
        this.gridApi.setGridOption("rowData", rows);
      })
      .catch((error) => {
        if (generation !== this.dataFetchGeneration) return;
        console.error("[AgGridComponent] refreshRowData error:", error);
      });
  }

  onFilterTextBoxChanged() {
    if (!this.gridApi || !this.hasQuickFilterInputTarget) return;
    this.gridApi.setGridOption("quickFilterText", this.quickFilterInputTarget.value);
  }

  switchToBasic() {
    if (!this.gridApi) return;
    this.isBasicMode = true;
    this.gridApi.setGridOption("loading", true);
    this.setActiveToggle("basic");
    this.setBasicModeColumnOptions();
    if (this.hasFilterToggleTarget) {
      this.gridApi.closeToolPanel();
      this.filterToggleTarget.style.display = "none";
      this.filterToggleTarget.setAttribute("aria-pressed", "false");
    }
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.style.display = "none";
      this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    }
    if (this.hasExportToggleTarget) {
      this.closeExportDropdown();
    }
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.syncDefaultExportParams();
    this.gridApi?.clearCellSelection();
    this.fetchRowDataWithColumnDefs(this.rowDataUrlValue, this.basicColDefs(this.columnDefsValue));
  }

  switchToAdvanced() {
    if (!this.gridApi) return;
    this.isBasicMode = false;
    this.gridApi.setGridOption("loading", true);
    this.setActiveToggle("advanced");
    this.setAdvancedModeColumnOptions();
    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.style.display = "";
    }
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.style.display = "";
    }
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.syncDefaultExportParams();
    this.gridApi?.clearCellSelection();
    this.fetchRowDataWithColumnDefs(this.advancedRowDataUrlValue, this.advancedColumnDefsValue);
  }

  // unknown, not string: this is also bound directly to the Export button's click handler, which passes a MouseEvent
  exportCsv(titleOverride?: unknown) {
    const title = this.resolveExportTitle(titleOverride);
    if (this.hasActiveRange) {
      this.exportRangeData("csv", title);
      return;
    }
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    const processRowGroupCallback = (params: ProcessRowGroupForExportParams) => formatGroupCellValue(params);
    const prependContent = this.csvPrependContent(title);
    if (this.hasRowSelection) {
      this.gridApi?.exportDataAsCsv({ ...this.buildSelectionExportParams(), processCellCallback, processRowGroupCallback, prependContent });
    } else {
      this.gridApi?.exportDataAsCsv({ processCellCallback, processRowGroupCallback, prependContent });
    }
    this.closeExportDropdown();
  }

  exportExcel(titleOverride?: unknown) {
    const title = this.resolveExportTitle(titleOverride);
    if (this.hasActiveRange) {
      this.exportRangeData("excel", title);
      return;
    }
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    const processRowGroupCallback = (params: ProcessRowGroupForExportParams) => formatGroupCellValue(params);
    const prependContent = this.excelPrependContent(title);
    if (this.hasRowSelection) {
      this.gridApi?.exportDataAsExcel({ ...this.buildSelectionExportParams(), processCellCallback, processRowGroupCallback, prependContent });
    } else {
      this.gridApi?.exportDataAsExcel({ processCellCallback, processRowGroupCallback, prependContent });
    }
    this.closeExportDropdown();
  }

  // Generic confirm-then-POST for server-side async exports; reached via Stimulus outlet since
  // app/javascript can't import app/components modules directly (tsconfig rootDir).
  async confirmAndPost(url: string, options: ConfirmModalOptions): Promise<boolean> {
    if (!url) return false;
    const ok = await showConfirmModal(options);
    if (!ok) return false;

    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return true;
    } catch (error) {
      console.error("[AgGridComponent] confirmAndPost error:", error);
      alert("Export failed. Please try again.");
      return false;
    }
  }

  // Explicit caller-supplied title (e.g. vote-by-vote's question name) wins; otherwise falls
  // back to the component-level exportTitleValue (e.g. election name), if any.
  private resolveExportTitle(explicit?: unknown): string | undefined {
    if (typeof explicit === "string" && explicit) return explicit;
    return this.exportTitleValue || undefined;
  }

  // CSV prependContent is built as cells (not a raw string) so AG Grid applies its own
  // CSV escaping/quoting to the question name.
  private csvPrependContent(title: string | undefined): CsvCustomContent | undefined {
    return title ? [[{ data: { value: title } }]] : undefined;
  }

  private excelPrependContent(title: string | undefined): ExcelRow[] | undefined {
    return title ? [{ cells: [{ data: { type: "String", value: title } }] }] : undefined;
  }

  // Resets sort/pin/filter state so it doesn't carry over onto the new question's same-named column.
  fetchQuestionData(url: string): void {
    if (!this.gridApi) return;
    this.gridApi.setGridOption("loading", true);
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.syncDefaultExportParams();
    this.gridApi.clearCellSelection();
    this.gridApi.setFilterModel(null);
    this.gridApi.resetColumnState();
    if (this.hasQuickFilterInputTarget) {
      this.quickFilterInputTarget.value = "";
      this.gridApi.setGridOption("quickFilterText", "");
    }
    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch question data: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (!this.gridApi) return;
        this.pendingAutoSize = true;
        this.gridApi.setGridOption("columnDefs", withNumberComparator(withDateFilterComparator(json.column_defs ?? [])));
        this.gridApi.setGridOption("rowData", json.rows ?? []);
        this.gridApi.setGridOption("pinnedBottomRowData", json.footer_row ?? []);
        this.gridApi.setGridOption("loading", false);
      })
      .catch((error) => {
        console.error("[AgGridComponent] fetchQuestionData error:", error);
        this.gridApi?.setGridOption("loading", false);
      });
  }

  private setBasicModeColumnOptions() {
    if (!this.gridApi) return;
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { filterParams: ebFilterParams, ...baseColDef, suppressHeaderMenuButton: true });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", true);
    if (Object.keys(this.basicModeOptionsValue).length > 0) {
      this.gridApi.updateGridOptions(this.basicModeOptionsValue);
    }
  }

  private setAdvancedModeColumnOptions() {
    if (!this.gridApi) return;
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { filterParams: ebFilterParams, ...baseColDef });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", false);
    if (Object.keys(this.advancedModeOptionsValue).length > 0) {
      this.gridApi.updateGridOptions(this.advancedModeOptionsValue);
    }
  }

  private handleRangeSelectionChanged() {
    const ranges = this.gridApi?.getCellRanges();
    if (!ranges || ranges.length === 0) {
      this.hasActiveRange = false;
      this.syncDefaultExportParams();
      return;
    }
    const range = ranges[0];
    const startRow = range.startRow?.rowIndex ?? 0;
    const endRow = range.endRow?.rowIndex ?? 0;
    const rowSpan = Math.abs(endRow - startRow) + 1;
    this.hasActiveRange = rowSpan > 1 || range.columns.length > 1;
    this.syncDefaultExportParams();
  }

  // Columns/rows to restrict an export to, based on the active cell range.
  // Shared by exportRangeData and syncDefaultExportParams.
  private buildRangeExportParams(): {
    columnKeys: string[];
    shouldRowBeSkipped: (params: { node: IRowNode }) => boolean;
  } | null {
    if (!this.gridApi) return null;
    const ranges = this.gridApi.getCellRanges();
    if (!ranges || ranges.length === 0) return null;
    const range = ranges[0];
    const startIsPinned = range.startRow!.rowPinned === "bottom";
    const endIsPinned = range.endRow!.rowPinned === "bottom";
    const includeFooter = startIsPinned || endIsPinned;
    const columnKeys = range.columns.map((col) => col.getColId());

    let startRow: number;
    let endRow: number;

    if (startIsPinned && endIsPinned) {
      startRow = 0;
      endRow = -1;
    } else if (endIsPinned) {
      startRow = range.startRow!.rowIndex;
      endRow = this.gridApi.getDisplayedRowCount() - 1;
    } else if (startIsPinned) {
      startRow = 0;
      endRow = range.endRow!.rowIndex;
    } else {
      const rawStart = range.startRow!.rowIndex;
      const rawEnd = range.endRow!.rowIndex;
      startRow = Math.min(rawStart, rawEnd);
      endRow = Math.max(rawStart, rawEnd);
    }

    return {
      columnKeys,
      shouldRowBeSkipped: ({ node }: { node: IRowNode }) => {
        if (node.rowPinned === "bottom") return !includeFooter;
        const idx = node.rowIndex ?? -1;
        return idx < startRow || idx > endRow;
      },
    };
  }

  // Replicates onlySelected's row filtering, but exempts the footer — AG Grid's
  // own onlySelected always drops pinned rows since they can never be "selected".
  private buildSelectionExportParams(): {
    shouldRowBeSkipped: (params: { node: IRowNode }) => boolean;
  } {
    return {
      shouldRowBeSkipped: ({ node }: { node: IRowNode }) => {
        if (node.rowPinned === "bottom") return false;
        return !node.isSelected();
      },
    };
  }

  // Keeps default export params in sync so the native right-click "Export" menu
  // (which bypasses exportCsv/exportExcel entirely) also respects selection.
  private syncDefaultExportParams() {
    if (!this.gridApi) return;
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    const processRowGroupCallback = (params: ProcessRowGroupForExportParams) => formatGroupCellValue(params);

    let scopeParams: Record<string, unknown> = {};
    if (this.hasActiveRange) {
      scopeParams = this.buildRangeExportParams() ?? {};
    } else if (this.hasRowSelection) {
      scopeParams = this.buildSelectionExportParams();
    }

    const params = { processCellCallback, processRowGroupCallback, ...scopeParams };
    this.gridApi.setGridOption("defaultCsvExportParams", params);
    this.gridApi.setGridOption("defaultExcelExportParams", params);
  }

  private exportRangeData(format: "csv" | "excel", title?: string) {
    if (!this.gridApi) return;
    const rangeParams = this.buildRangeExportParams();
    if (!rangeParams) return;
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    const processRowGroupCallback = (params: ProcessRowGroupForExportParams) => formatGroupCellValue(params);

    if (format === "csv") {
      const prependContent = this.csvPrependContent(title);
      this.gridApi.exportDataAsCsv({ ...rangeParams, processCellCallback, processRowGroupCallback, prependContent });
    } else {
      const prependContent = this.excelPrependContent(title);
      this.gridApi.exportDataAsExcel({ ...rangeParams, processCellCallback, processRowGroupCallback, prependContent });
    }
    this.closeExportDropdown();
  }

  private loadGridState() {
    if (!this.gridStateUrlValue || !this.gridApi) return;
    fetch(this.gridStateUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) return;
        return response.json();
      })
      .then((json) => {
        if (!json?.state || !this.gridApi) return;
        const { mode, ...agState } = json.state as Record<string, unknown>;
        if (Object.keys(agState).length === 0) return;
        const hasAdvanced = this.advancedColumnDefsValue.length > 0;
        this.isRestoringState = true;
        if (mode === "advanced" && hasAdvanced) {
          this.pendingRestoreState = agState as ReturnType<GridApi["getState"]>;
          this.switchToAdvanced();
        } else {
          this.gridApi.setState(agState as ReturnType<GridApi["getState"]>);
          requestAnimationFrame(() => {
            this.isRestoringState = false;
            this.syncPanelButtonStates();
          });
        }
      })
      .catch(() => {
        // Non-critical — grid functions without saved state
      });
  }

  private applyPendingRestoreState() {
    if (!this.pendingRestoreState || !this.gridApi) return;
    const state = this.pendingRestoreState;
    this.pendingRestoreState = null;
    this.gridApi.setState(state);
    requestAnimationFrame(() => {
      this.isRestoringState = false;
      this.syncPanelButtonStates();
    });
  }

  private scheduleSaveGridState() {
    if (!this.gridStateUrlValue || this.isRestoringState) return;
    if (this.saveGridStateTimer !== null) {
      clearTimeout(this.saveGridStateTimer);
    }
    this.saveGridStateTimer = setTimeout(() => {
      this.saveGridStateTimer = null;
      this.persistGridState();
    }, 2000);
  }

  private persistGridState() {
    if (!this.gridStateUrlValue || !this.gridApi) return;
    const state = {
      ...this.gridApi.getState(),
      mode: this.isBasicMode ? "basic" : "advanced",
    };
    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    fetch(this.gridStateUrlValue, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify({ state }),
    }).catch(() => {
      // Non-critical — state save failure is silent
    });
  }

  private fetchRowData(url: string) {
    this.fetchRowDataWithColumnDefs(url, null);
  }

  private fetchRowDataWithColumnDefs(url: string, columnDefs: ColDef[] | null) {
    const generation = ++this.dataFetchGeneration;
    fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch row data: ${response.status}`);
        }
        return response.json();
      })
      .then((json) => {
        if (generation !== this.dataFetchGeneration || !this.gridApi) return;
        const rows = Array.isArray(json) ? json : (json.rows ?? []);
        if (columnDefs !== null) {
          this.gridApi.setGridOption("columnDefs", withNumberComparator(withDateFilterComparator(columnDefs)));
        } else if (!Array.isArray(json) && json.column_defs) {
          this.gridApi.setGridOption("columnDefs", withNumberComparator(withDateFilterComparator(json.column_defs)));
        }
        this.totalRowCount = rows.length;
        this.pendingAutoSize = true;
        this.gridApi.setGridOption("rowData", rows);
        const hasFooterConfig = !!(this.footerCountsValue.labelField || this.footerCountsValue.sumFields?.length);
        if (!hasFooterConfig) {
          this.gridApi.setGridOption(
            "pinnedBottomRowData",
            Array.isArray(json) ? [] : (json.footer_row ?? [])
          );
        }
        this.gridApi.setGridOption("loading", false);
        this.applyPendingRestoreState();
      })
      .catch((error) => {
        if (generation !== this.dataFetchGeneration) return;
        console.error("[AgGridComponent] fetchRowData error:", error);
        this.gridApi?.setGridOption("loading", false);
        this.pendingRestoreState = null;
        this.isRestoringState = false;
      });
  }

  private handleSelectionChanged(event: SelectionChangedEvent) {
    const selectedRows = event.api.getSelectedRows() as Array<Record<string, unknown>>;
    const count = selectedRows.length;
    this.hasRowSelection = count > 0;
    this.syncDefaultExportParams();

    if (!this.hasActionsMenuTarget) return;

    if (count > 0) {
      this.actionsMenuTarget.classList.add("visible");
      this.actionsMenuTarget.style.display = ""; // clear any leftover hide from scroll-tracking
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = `${count} selected`;
      }
      this.renderActions(selectedRows);
      if (this.followSelectionValue) {
        this.updateFollowScrollVisibility();
      }
    } else {
      this.actionsMenuTarget.classList.remove("visible");
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = "";
      }
      this.clearActions();
      if (this.followSelectionValue) {
        this.resetMenuPosition();
      }
    }
  }

  private refreshFooterRow(): void {
    if (!this.gridApi) return;
    const config = this.footerCountsValue;
    if (!config.labelField && !config.sumFields?.length) return;

    const displayedCount = this.gridApi.getDisplayedRowCount();
    const footerRow: Record<string, unknown> = {};

    if (config.labelField) {
      const label = config.countLabel ?? "rows";
      footerRow[config.labelField] =
        displayedCount === this.totalRowCount
          ? `${this.totalRowCount} ${label}`
          : `${displayedCount} of ${this.totalRowCount} ${label}`;
    }

    if (config.sumFields && config.sumFields.length > 0) {
      const sums: Record<string, number> = {};
      config.sumFields.forEach((f: string) => { sums[f] = 0; });
      this.gridApi.forEachNodeAfterFilter((node) => {
        if (!node.data) return;
        config.sumFields!.forEach((f: string) => {
          sums[f] += Number((node.data as Record<string, unknown>)[f] ?? 0);
        });
      });
      Object.assign(footerRow, sums);
    }

    this.gridApi.setGridOption("pinnedBottomRowData", [footerRow]);
  }
}
